/**
 * ManagerApprovalQueue.tsx
 * Page for managers to review and approve/reject on-hold items from receiving.
 * On-hold items require manager approval before they can be used in production.
 * - Approved items become available for next day's production
 * - Rejected items are marked for return to warehouse
 */
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { inventoryService, PendingApprovalLot } from '../../services/inventoryService';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  CheckCircle,
  XCircle,
  Package,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';
import { formatDateTimeLocal } from '../../utils/dateUtils';

// Map on_hold_reason codes to i18n keys
const HOLD_REASON_KEYS: Record<string, string> = {
  temperature: 'transferVerification.onHoldReasons.temperature',
  damaged: 'transferVerification.onHoldReasons.damaged',
  wrong_quantity: 'transferVerification.onHoldReasons.wrongQuantity',
  wrongItemTomorrow: 'transferVerification.onHoldReasons.wrongItemTomorrow',
  quality: 'transferVerification.onHoldReasons.quality',
  expiration: 'transferVerification.onHoldReasons.expiration',
  other: 'transferVerification.onHoldReasons.other',
};

export interface ManagerApprovalQueueHandle {
  refresh: () => void;
  loading: boolean;
}

const ManagerApprovalQueue = forwardRef<ManagerApprovalQueueHandle>((_, ref) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [pendingItems, setPendingItems] = useState<PendingApprovalLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Modal state for approval/rejection
  const [modalLot, setModalLot] = useState<PendingApprovalLot | null>(null);
  const [modalAction, setModalAction] = useState<'approve' | 'reject' | null>(null);
  const [modalNotes, setModalNotes] = useState('');

  // Expose refresh and loading to parent
  useImperativeHandle(ref, () => ({
    refresh: () => fetchPendingApprovals(),
    loading,
  }));

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const items = await inventoryService.getPendingApprovals();
      setPendingItems(items);
    } catch (error: any) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error(t('managerApproval.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // Handle approve action
  const handleApprove = async () => {
    if (!modalLot || !user) return;

    setActionLoading(modalLot.id);
    try {
      await inventoryService.approveLot(modalLot.id, user.username || user.email || user.id, modalNotes || undefined);
      toast.success(t('managerApproval.approved'));
      setPendingItems(prev => prev.filter(item => item.id !== modalLot.id));
      closeModal();
    } catch (error: any) {
      console.error('Failed to approve lot:', error);
      toast.error(t('managerApproval.approveError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject action
  const handleReject = async () => {
    if (!modalLot || !user) return;

    if (!modalNotes.trim()) {
      toast.error(t('managerApproval.rejectionNotesRequired'));
      return;
    }

    setActionLoading(modalLot.id);
    try {
      await inventoryService.rejectLot(modalLot.id, user.username || user.email || user.id, modalNotes);
      toast.success(t('managerApproval.rejected'));
      setPendingItems(prev => prev.filter(item => item.id !== modalLot.id));
      closeModal();
    } catch (error: any) {
      console.error('Failed to reject lot:', error);
      toast.error(t('managerApproval.rejectError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Open modal for action
  const openModal = (lot: PendingApprovalLot, action: 'approve' | 'reject') => {
    setModalLot(lot);
    setModalAction(action);
    setModalNotes('');
  };

  // Close modal
  const closeModal = () => {
    setModalLot(null);
    setModalAction(null);
    setModalNotes('');
  };


  // Get translated hold reason
  const getHoldReasonText = (reason: string) => {
    const key = HOLD_REASON_KEYS[reason];
    return key ? t(key) : reason;
  };

  return (
    <div className="px-4 pb-4">
      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-6">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && pendingItems.length === 0 && (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            {t('managerApproval.noPendingItems')}
          </p>
        </div>
      )}

      {/* Pending items list */}
      {!loading && pendingItems.length > 0 && (
        <div className={`gap-3 ${pendingItems.length > 1 ? 'grid grid-cols-1 xl:grid-cols-2' : 'space-y-3'}`}>
          {pendingItems.map((item) => (
            <div
              key={item.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700 p-3 shadow-sm"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Item info */}
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-amber-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {item.item_number}
                        {item.item_english_name && (
                          <span> - {item.item_english_name}</span>
                        )}
                        {item.item_chinese_name && (
                          <span className="text-gray-500 dark:text-gray-400 font-normal ml-2">
                            ({item.item_chinese_name})
                          </span>
                        )}
                      </h3>
                    </div>
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 mt-2 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 block">
                        {t('managerApproval.transferNumber')}
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white">
                        {item.transfer_number}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 block">
                        {t('managerApproval.quantity')}
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white">
                        {Math.round(Number(item.actual_quantity))}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 block">
                        {t('managerApproval.weight')}
                      </span>
                      <span className="font-mono text-gray-900 dark:text-white">
                        {parseFloat(String(item.actual_weight)).toFixed(2)} lbs
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 block">
                        {t('managerApproval.receivedBy')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {item.verified_by_name || item.verified_by}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 block">
                        {t('managerApproval.receivedAt')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {formatDateTimeLocal(item.verified_at)}
                      </span>
                    </div>
                  </div>

                  {/* Hold reason + Notes */}
                  <div className="mt-2 flex items-center gap-3 min-w-0">
                    <div className="flex items-center gap-1.5 shrink-0">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                        {t('managerApproval.holdReason')}: {getHoldReasonText(item.on_hold_reason)}
                      </span>
                    </div>
                    {item.verification_notes && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-1.5 py-0.5 rounded truncate">
                        <span className="font-medium">{t('managerApproval.notes')}:</span> {item.verification_notes}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 md:flex-col">
                  <button
                    onClick={() => openModal(item, 'approve')}
                    disabled={actionLoading === item.id}
                    className="flex-1 md:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <Check className="w-4 h-4" />
                    {t('managerApproval.approve')}
                  </button>
                  <button
                    onClick={() => openModal(item, 'reject')}
                    disabled={actionLoading === item.id}
                    className="flex-1 md:flex-none px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    {t('managerApproval.reject')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approval/Rejection Modal */}
      {modalLot && modalAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              {modalAction === 'approve' ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {modalAction === 'approve' ? t('managerApproval.approveConfirm') : t('managerApproval.rejectConfirm')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {modalLot.item_english_name || modalLot.item_number}
                </p>
              </div>
            </div>

            {/* Notes input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {modalAction === 'approve' ? t('managerApproval.approvalNotes') : t('managerApproval.rejectionNotes')}
              </label>
              <textarea
                value={modalNotes}
                onChange={(e) => setModalNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                rows={3}
                placeholder={modalAction === 'reject' ? t('managerApproval.rejectionNotesRequired') : ''}
                required={modalAction === 'reject'}
              />
            </div>

            {/* Modal buttons */}
            <div className="flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={modalAction === 'approve' ? handleApprove : handleReject}
                disabled={actionLoading === modalLot.id || (modalAction === 'reject' && !modalNotes.trim())}
                className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                  modalAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLoading === modalLot.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : modalAction === 'approve' ? (
                  <>
                    <Check className="w-4 h-4" />
                    {t('managerApproval.approve')}
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    {t('managerApproval.reject')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ManagerApprovalQueue;
